import React, { useState, useMemo } from 'react';
import { Building2, Plus, ChevronRight } from 'lucide-react';
import { STATUS_CONFIG, STATUS_COLOR_CLASSES, URGENCY_OPTIONS } from './constants';

// Request Card Component
/** Purpose: Renders request card. Inputs: { request, onClick }. Output: React render output (JSX). */
function RequestCard({ request, onClick }) {
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.DRAFT;
  const colorClasses = STATUS_COLOR_CLASSES[statusConfig.color] || STATUS_COLOR_CLASSES.slate;

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl border border-slate-200 p-4 text-left hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
          <Building2 className={`w-5 h-5 ${colorClasses.text}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900">{request.providerName}</h4>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClasses.badge}`}>
              {statusConfig.label}
            </span>
          </div>
          <p className="text-sm text-slate-600">{request.organization}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
            <span>{request.recordTypes?.length || 0} record type(s)</span>
            <span>Created {new Date(request.createdAt).toLocaleDateString()}</span>
            {request.sentAt && <span>Sent {new Date(request.sentAt).toLocaleDateString()}</span>}
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </button>
  );
}

// Request List Component
/** Purpose: Renders request list. Inputs: { requests, onNewRequest, onSelectRequest }. Output: React render output (JSX). */
export default function RequestList({ requests, onNewRequest, onSelectRequest }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');

  const providerOptions = useMemo(() => {
    const values = new Set();
    requests.forEach((req) => {
      const label = [req.providerName, req.organization].filter(Boolean).join(' - ');
      if (label) values.add(label);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const filteredRequests = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((req) => {
      if (statusFilter !== 'all' && req.status !== statusFilter) return false;
      if (urgencyFilter !== 'all' && (req.urgency || 'routine') !== urgencyFilter) return false;
      if (providerFilter !== 'all') {
        const label = [req.providerName, req.organization].filter(Boolean).join(' - ');
        if (label !== providerFilter) return false;
      }
      if (!query) return true;
      const haystack = [
        req.providerName,
        req.organization,
        req.email,
        req.fax,
        req.notes,
        req.specificRecords,
        (req.recordTypes || []).join(' ')
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [requests, search, statusFilter, providerFilter, urgencyFilter]);

  const activeRequests = filteredRequests.filter(r => !['RECEIVED', 'DENIED', 'CANCELLED'].includes(r.status));
  const completedRequests = filteredRequests.filter(r => ['RECEIVED', 'DENIED', 'CANCELLED'].includes(r.status));
  const filtersActive = Boolean(
    search.trim()
    || statusFilter !== 'all'
    || providerFilter !== 'all'
    || urgencyFilter !== 'all'
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Provider Requests</h3>
          <p className="text-sm text-slate-600">Request medical records from healthcare providers</p>
        </div>
        <button
          onClick={onNewRequest}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Request
        </button>
      </div>

      {requests.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search provider, org, notes, record types"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="all">All</option>
                {Object.keys(STATUS_CONFIG).map((status) => (
                  <option key={status} value={status}>
                    {STATUS_CONFIG[status].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Urgency</label>
              <select
                value={urgencyFilter}
                onChange={(e) => setUrgencyFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="all">All</option>
                {URGENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Provider</label>
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="all">All</option>
                {providerOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2 flex justify-end">
              {filtersActive && (
                <button
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('all');
                    setProviderFilter('all');
                    setUrgencyFilter('all');
                  }}
                  className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-2">No provider requests yet</p>
          <p className="text-sm text-slate-400 mb-4">
            Request your medical records from doctors, hospitals, and clinics
          </p>
          <div className="max-w-lg mx-auto text-left bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">First Request Checklist</p>
            <ol className="text-sm text-slate-700 space-y-1">
              <li>1. Add provider and organization details.</li>
              <li>2. Choose record types and date range.</li>
              <li>3. Review, send, and track provider response status.</li>
            </ol>
          </div>
          <button
            onClick={onNewRequest}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
          >
            Create Your First Request
          </button>
        </div>
      ) : (
        <>
          {activeRequests.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Active Requests ({activeRequests.length})</h4>
              <div className="space-y-3">
                {activeRequests.map(request => (
                  <RequestCard key={request.id} request={request} onClick={() => onSelectRequest(request)} />
                ))}
              </div>
            </div>
          )}

          {completedRequests.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Completed ({completedRequests.length})</h4>
              <div className="space-y-3">
                {completedRequests.map(request => (
                  <RequestCard key={request.id} request={request} onClick={() => onSelectRequest(request)} />
                ))}
              </div>
            </div>
          )}
          {activeRequests.length === 0 && completedRequests.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-slate-600">No requests match your filters.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
